/**
 * The two searchable live pickers of the Basics tab: Image (Azure Marketplace, curated
 * publishers) and Size (Azure SKU list with the monthly compute estimate when the price
 * list has one). Both search through the debounced backend query.
 */
import { useRef, useState } from 'react'
import { useAzureCatalog, useSelectedSize } from '@/hooks/useAppriseAzure'
import { ReadOnlyField } from './FormFields'
import { formatCurrency } from '@/utils/format'
import type { AzureImage, AzureVmSize } from '@/types'
import { DEFAULT_IMAGE, DEFAULT_IMAGE_LABEL, imageRefString, sameImage } from './appriseCatalog'
import { OS_LABELS } from './appriseReview'
import { Badge } from './AppriseFields'
import { imageSizeProblem } from './appriseSizeCheck'
import { SearchableSelect, type ComboOption } from './LiveSelect'
import styles from './AppriseCreateVm.module.css'
import type { AppriseVmFormApi } from './useAppriseVmForm'

const priceText = (s: AzureVmSize | undefined) =>
  s && s.price_month_usd != null ? `~${formatCurrency(s.price_month_usd)}/month` : 'Estimate unavailable'

export function ImagePicker({ form }: { form: AppriseVmFormApi }) {
  const [q, setQ] = useState('')
  const images = useAzureCatalog('images', { q, location: form.location }, form.enabled)
  // remember every image seen so the summary keeps its friendly label while the user searches
  const known = useRef(new Map<string, AzureImage>())
  for (const i of images.items) known.current.set(imageRefString(i), i)

  const s = form.config.server
  const key = imageRefString(s.image)
  const current = known.current.get(key)
  const label = current?.label
    ?? (form.catalog.image && sameImage(form.catalog.image, s.image) ? form.catalog.image.label : undefined)
    ?? form.options.images.find((i) => sameImage(i, s.image))?.label
    ?? (sameImage(DEFAULT_IMAGE, s.image) ? DEFAULT_IMAGE_LABEL : key)

  // Azure says which images the picked VM size can run (Hyper-V generation, architecture): the others are greyed out
  const options: ComboOption[] = images.items.map((i) => {
    const problem = imageSizeProblem(i, form.pickedSize)
    const generation = i.hyperv_generation ? (i.hyperv_generation.toUpperCase() === 'V2' ? 'Gen 2' : 'Gen 1') : 'Gen unknown'
    return {
      id: imageRefString(i),
      label: i.label,
      description: `${imageRefString(i)} · ${OS_LABELS[i.os_type] ?? i.os_type} · ${generation} · ${i.architecture}`
        + (form.pickedSize ? (problem ? ` · not supported by ${form.pickedSize.name}` : ` · works with ${form.pickedSize.name}`) : ''),
      disabled: !!problem,
      selected: sameImage(i, s.image),
    }
  })

  return (
    <SearchableSelect
      label="Image" required full catalog={images} options={options}
      placeholder="Search images (e.g. Windows Server 2022, Ubuntu)…"
      emptyText="No images match — try another search"
      error={form.validation.errors['server.image']}
      onSearch={setQ}
      onSelect={(id) => { const img = known.current.get(id); if (img) form.chooseImage(img) }}
      summary={(
        <>
          <span><strong>{label}</strong>{form.validation.warnings['server.image'] && <Badge>Not verified for Apprise</Badge>}</span>
          <span className={styles.mono}>{key}</span>
          <span>Operating system: {OS_LABELS[s.os_type] ?? s.os_type}</span>
        </>
      )}
    />
  )
}

export function SizePicker({ form }: { form: AppriseVmFormApi }) {
  const [q, setQ] = useState('')
  const sizes = useAzureCatalog('sizes', { q, location: form.location }, form.enabled)
  const vmSize = form.config.server.vm_size
  const { size: selected, catalog: selectedCatalog } = useSelectedSize(form.location, vmSize, form.enabled)
  const err = form.validation.errors['server.vm_size']

  if (!form.catalog.overrides_allowed) {
    return (
      <ReadOnlyField label="Size" value={vmSize}
        hint={`Fixed by the catalog: ${form.catalog.vcpu} vCPU, ${form.catalog.memory_gb} GiB. This SKU does not allow VM size overrides.`} />
    )
  }

  // Azure says which sizes can run the picked image: the others are greyed out with the reason
  const options: ComboOption[] = sizes.items.map((z) => {
    const imageProblem = imageSizeProblem(form.pickedImage, z)
    const generations = z.hyperv_generations.map((g) => (g.toUpperCase() === 'V2' ? 'Gen 2' : 'Gen 1')).join(' + ')
    return {
      id: z.name,
      label: `${z.name} — ${z.vcpus} vCPU, ${z.memory_gb} GiB`,
      description: !z.available
        ? `Not available${z.restrictions.length ? ` (${z.restrictions.join(', ')})` : ''} in this region for this subscription`
        : imageProblem
          ? `Does not run ${form.pickedImage?.label ?? 'the selected image'}: supports ${generations || 'other generations'} only`
          : priceText(z) + (z.quota && !z.quota.enough ? ' · not enough vCPU quota' : '') + (generations ? ` · ${generations}` : ''),
      disabled: !z.available || !!imageProblem,
      selected: z.name === vmSize,
    }
  })

  return (
    <SearchableSelect
      label="Size" required full catalog={sizes} options={options} error={err} warning={form.validation.warnings['server.vm_size']}
      placeholder="Search sizes (e.g. D4s, B2)…"
      emptyText="No sizes match — try another search"
      onSearch={setQ}
      onSelect={(name) => form.setServer({ vm_size: name })}
      summary={(
        <>
          <span><strong className={styles.mono}>{vmSize}</strong>{selected ? ` — ${selected.vcpus} vCPU, ${selected.memory_gb} GiB RAM` : ''}</span>
          {selected && <span>Up to {selected.max_data_disks} data disks · {selected.zones.length ? `zones ${selected.zones.join(', ')}` : 'no availability zones'}</span>}
          {selected?.quota && selected.available && selected.quota.enough && (
            <span>vCPU quota: {Math.min(...[selected.quota.family_free, selected.quota.regional_free].filter((n): n is number => n != null))} free (needs {selected.vcpus})</span>
          )}
          <span>Estimated cost: <strong>{selectedCatalog.isLoading ? 'checking…' : priceText(selected)}</strong></span>
          {selected?.price_note && <span className={styles.muted}>{selected.price_note}</span>}
        </>
      )}
    />
  )
}
